import { create } from "zustand";

import {
  formatSchoolReportDate,
  type SchoolReportEntry,
  type SchoolReportGrade,
  type SchoolReportMarksFormValues,
  type SchoolReportPendingDetails,
} from "../types/schoolReports";

export type SchoolReportEditTarget = {
  report: SchoolReportEntry;
  gradeId: string;
  gradeLabel: string;
};

type SchoolReportsState = {
  grades: SchoolReportGrade[];
  pendingReport: SchoolReportPendingDetails | null;
  preselectedGradeId: string | null;
  setGrades: (grades: SchoolReportGrade[]) => void;
  setPendingReport: (report: SchoolReportPendingDetails) => void;
  clearPendingReport: () => void;
  startAddExam: (
    gradeId: string,
    gradeLabel: string,
    boardId?: string,
    boardLabel?: string,
    schoolId?: string,
    schoolLabel?: string,
  ) => void;
  setPendingExamType: (examTypeId: string, examTypeLabel: string) => void;
  setPreselectedGradeId: (gradeId: string | null) => void;
  saveReport: (marks: SchoolReportMarksFormValues) => string | null;
  updateReport: (reportId: string, marks: SchoolReportMarksFormValues) => void;
  deleteReport: (reportId: string) => void;
  resetSchoolReports: () => void;
};

function createReportId(): string {
  return `report-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const useSchoolReportsStore = create<SchoolReportsState>((set, get) => ({
  grades: [],
  pendingReport: null,
  preselectedGradeId: null,

  setGrades: (grades) => set({ grades }),

  setPendingReport: (report) => set({ pendingReport: report }),

  clearPendingReport: () => set({ pendingReport: null }),

  startAddExam: (
    gradeId,
    gradeLabel,
    boardId = "",
    boardLabel = "",
    schoolId = "",
    schoolLabel,
  ) =>
    set({
      pendingReport: {
        gradeId,
        gradeLabel,
        boardId,
        boardLabel,
        examTypeId: "",
        examTypeLabel: "",
        schoolId,
        schoolLabel,
        isAddExamFlow: true,
      },
    }),

  setPendingExamType: (examTypeId, examTypeLabel) =>
    set((state) =>
      state.pendingReport
        ? {
            pendingReport: {
              ...state.pendingReport,
              examTypeId,
              examTypeLabel,
            },
          }
        : {},
    ),

  setPreselectedGradeId: (gradeId) => set({ preselectedGradeId: gradeId }),

  saveReport: (marks) => {
    const pendingReport = get().pendingReport;
    if (!pendingReport) {
      return null;
    }

    const reportId = createReportId();
    const entryType = marks.marksheetFile ? "file" : "manual";
    const newEntry: SchoolReportEntry = {
      id: reportId,
      title: pendingReport.examTypeLabel,
      type: entryType,
      date: formatSchoolReportDate(new Date()),
      subjects: marks.subjects,
    };

    set((state) => {
      const existingGradeIndex = state.grades.findIndex(
        (grade) => grade.gradeLabel === pendingReport.gradeLabel,
      );

      const nextGrades =
        existingGradeIndex === -1
          ? [
              ...state.grades,
              {
                id: pendingReport.gradeId,
                gradeLabel: pendingReport.gradeLabel,
                reports: [newEntry],
              },
            ]
          : state.grades.map((grade, index) =>
              index === existingGradeIndex
                ? { ...grade, reports: [...grade.reports, newEntry] }
                : grade,
            );

      return {
        grades: nextGrades,
        pendingReport: null,
      };
    });

    return reportId;
  },

  updateReport: (reportId, marks) => {
    const hasMarksheet = Boolean(marks.marksheetFileKey?.trim());
    set((state) => ({
      grades: state.grades.map((grade) => ({
        ...grade,
        reports: grade.reports.map((report) =>
          report.id === reportId
            ? {
                ...report,
                type: hasMarksheet ? "file" : "manual",
                fileKey: hasMarksheet ? (marks.marksheetFileKey ?? "") : "",
                subjects: marks.subjects,
                date: formatSchoolReportDate(new Date()),
              }
            : report,
        ),
      })),
    }));
  },

  deleteReport: (reportId) => {
    set((state) => ({
      grades: state.grades
        .map((grade) => ({
          ...grade,
          reports: grade.reports.filter((report) => report.id !== reportId),
        }))
        .filter((grade) => grade.reports.length > 0),
    }));
  },

  resetSchoolReports: () =>
    set({
      grades: [],
      pendingReport: null,
      preselectedGradeId: null,
    }),
}));

export function getSchoolReportById(
  reportId: string,
): SchoolReportEditTarget | null {
  const { grades } = useSchoolReportsStore.getState();
  for (const grade of grades) {
    const report = grade.reports.find((entry) => entry.id === reportId);
    if (report) {
      return { report, gradeId: grade.id, gradeLabel: grade.gradeLabel };
    }
  }
  return null;
}

export function useSchoolReportGrades() {
  return useSchoolReportsStore((state) => state.grades);
}

export function useSchoolReportPendingDetails() {
  return useSchoolReportsStore((state) => state.pendingReport);
}

export function useSchoolReportPreselectedGradeId() {
  return useSchoolReportsStore((state) => state.preselectedGradeId);
}

export function useSchoolReportsActions() {
  const setPendingReport = useSchoolReportsStore(
    (state) => state.setPendingReport,
  );
  const clearPendingReport = useSchoolReportsStore(
    (state) => state.clearPendingReport,
  );
  const startAddExam = useSchoolReportsStore((state) => state.startAddExam);
  const setPendingExamType = useSchoolReportsStore(
    (state) => state.setPendingExamType,
  );
  const setPreselectedGradeId = useSchoolReportsStore(
    (state) => state.setPreselectedGradeId,
  );
  const setGrades = useSchoolReportsStore((state) => state.setGrades);
  const saveReport = useSchoolReportsStore((state) => state.saveReport);
  const updateReport = useSchoolReportsStore((state) => state.updateReport);
  const deleteReport = useSchoolReportsStore((state) => state.deleteReport);
  const resetSchoolReports = useSchoolReportsStore(
    (state) => state.resetSchoolReports,
  );

  return {
    setPendingReport,
    clearPendingReport,
    startAddExam,
    setPendingExamType,
    setPreselectedGradeId,
    setGrades,
    saveReport,
    updateReport,
    deleteReport,
    resetSchoolReports,
  };
}
